import React from "react";

import { useMaterials } from "contexts/search/SearchContextProvider";
import { usePeriodIndex } from "hooks/periodIndex";
import { useTenant } from "hooks/settings";
import { useMixGrouping } from "hooks/useMixGrouping";
import { useMixQuantity } from "hooks/useMixQuantity";
import { useGetPlanResultsQuery } from "src/store/api/generatedApi";
import { planMixTableEditsSlice } from "src/store/slices/planMixesTableEditSlice";
import {
  selectSearchedSteelGradeIds,
  steelGradeSearchSlice,
} from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

import { PlanId } from "components/common/boundary/PlanId";
import { DeployTable } from "components/common/plan/DeployTable";
import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";
import { getDriMaterial, makeRows } from "components/common/plan/utils";

interface Props {
  planId: number;
}

const noop = () => {
  /* no-op: onChange not needed in read-only mode */
};

export const SpreadsheetTab = ({ planId }: Props) => {
  const tenantName = useTenant();
  const materials = useMaterials();
  const period = usePeriodIndex();
  const planResults = useGetPlanResultsQuery({ tenantName, planId, period });
  const dispatch = useAppDispatch();

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, planId, period, "deploy")
  );

  const mixGrouping = useMixGrouping();
  const mixQuantity = useMixQuantity();

  const periodResults = React.useMemo(() => {
    if (!planResults.currentData) return undefined;
    return planResults.currentData.periods[period.toString()]?.mixes ?? [];
  }, [planResults.currentData, period]);

  const preloadedSummaries = React.useMemo(() => {
    if (!periodResults) return undefined;
    return Object.fromEntries(
      periodResults.map((result) => [result.mix.id, result.mix.summary])
    );
  }, [periodResults]);

  const preloadedDris = React.useMemo(() => {
    if (!periodResults) return undefined;
    return Object.fromEntries(
      periodResults.map((result) => [
        result.mix.id,
        getDriMaterial(result.mix) ?? null,
      ])
    );
  }, [periodResults]);

  const tableData = React.useMemo(() => {
    if (
      !planResults.currentData ||
      !periodResults ||
      materials.status !== "success"
    )
      return null;

    const materialsList = materials.data.byIndex;
    const steelGrades = planResults.currentData.steel_grade_set.steel_grades;

    const tableMaterials = materialsList
      .filter((material) => material.addition_location === "basket")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: null,
      }));

    const driInventory = materialsList.some(
      (material) => material.addition_location === "hopper"
    )
      ? 0
      : null;

    const rows = makeRows(
      planId as PlanId,
      period,
      periodResults,
      steelGrades,
      mixQuantity
    );

    return { tableMaterials, driInventory, rows };
  }, [
    planResults.currentData,
    periodResults,
    materials,
    planId,
    period,
    mixQuantity,
  ]);

  React.useEffect(() => {
    if (periodResults) {
      const steelGradeNameToId = Object.fromEntries(
        (planResults.currentData?.steel_grade_set.steel_grades ?? []).map(
          (steelGrade) => [steelGrade.name, steelGrade.id]
        )
      );
      const allSteelGradeIds = periodResults.flatMap((result) =>
        result.production
          .map((item) => steelGradeNameToId[item.steel_grade_name])
          .filter((id): id is number => id !== undefined)
      );
      dispatch(
        steelGradeSearchSlice.actions.setSteelGrades([
          planId as PlanId,
          period,
          allSteelGradeIds,
          "deploy",
        ])
      );
    }
  }, [periodResults, planResults.currentData, dispatch, planId, period]);

  React.useEffect(() => {
    if (tableData) {
      dispatch(
        planMixTableEditsSlice.actions.set([
          planId as PlanId,
          period,
          tableData.rows,
        ])
      );
    }
  }, [tableData, dispatch, planId, period]);

  if (tableData) {
    return (
      <DeployTable
        planId={planId as PlanId}
        period={period}
        disableEdit
        hasEdited={false}
        rows={tableData.rows}
        materials={tableData.tableMaterials}
        selectedSteelGradeIds={selectedSteelGradeIds}
        onChange={noop}
        mixGrouping={mixGrouping}
        driInventory={tableData.driInventory}
        steelGradesWidth={130}
        showInventory={false}
        showSummary
        preloadedSummaries={preloadedSummaries}
        preloadedDris={preloadedDris}
      />
    );
  } else {
    return <EditableTableSkeleton />;
  }
};
